import React, { useEffect, useMemo, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import PayPalSurface from "./PayPalSurface";
import { Alert, Box, CircularProgress } from "@mui/material";

const PaypalButton = ({
	amount,
	description,
	clientId,
	payeeEmail,
	currency = "USD",
	metadata = {},
	disabled = false,
	onApprove,
	onError,
	style,
}) => {
	const [renderKey, setRenderKey] = useState(() => `${clientId}-${amount}-${currency}`);
	const [loading, setLoading] = useState(true);
	const [localError, setLocalError] = useState(null);

	useEffect(() => {
		setRenderKey(`${clientId}-${amount}-${currency}-${Date.now()}`);
	}, [clientId, amount, currency]);

	useEffect(() => {
		setLocalError(null);
	}, [clientId, amount]);

	const normalizedAmount = Math.max(Number(amount) || 0, 0).toFixed(2);

	const paypalOptions = useMemo(
		() => ({
			"client-id": clientId,
			currency,
			intent: "capture",
			components: "buttons",
			"data-sdk-integration-source": "button-factory",
		}),
		[clientId, currency]
	);

	if (!clientId) {
		return null;
	}

	const createOrder = (data, actions) => {
		if (Number(normalizedAmount) <= 0) {
			return Promise.reject(new Error("El monto debe ser mayor a cero."));
		}

		const purchaseUnit = {
			amount: {
				currency_code: currency,
				value: normalizedAmount,
			},
			description,
		};

		if (metadata.customId) {
			purchaseUnit.custom_id = metadata.customId;
		}

		if (payeeEmail) {
			purchaseUnit.payee = { email_address: payeeEmail };
		}

		return actions.order.create({
			purchase_units: [purchaseUnit],
			application_context: {
				shipping_preference: "NO_SHIPPING",
			},
		});
	};

	const handleApprove = (data, actions) =>
		actions.order.capture().then((details) => {
			setLoading(false);
			if (onApprove) {
				onApprove(details);
			}
		});

	const handleError = (err) => {
		console.error("PayPal button error", err);
		setLoading(false);
		setLocalError("Ocurrió un error al cargar PayPal. Intenta nuevamente.");
		if (onError) {
			onError(err);
		}
	};

	return (
		<PayPalScriptProvider options={paypalOptions} deferLoading={false}>
			<Box
				sx={{
					position: "relative",
					width: "100%",
					maxWidth: 420,
				}}
			>
				{loading && (
					<Box
						sx={{
							position: "absolute",
							inset: 0,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							zIndex: 1,
							pointerEvents: "none",
						}}
					>
						<CircularProgress size={28} />
					</Box>
				)}
				{localError && (
					<Alert severity="error" sx={{ mb: 2 }}>
						{localError}
					</Alert>
				)}
				<PayPalSurface>
					<PayPalButtons
						key={renderKey}
						style={style || { layout: "vertical", color: "blue", shape: "rect" }}
						disabled={disabled || Number(normalizedAmount) <= 0}
						forceReRender={[normalizedAmount, currency, description]}
						createOrder={createOrder}
						onApprove={handleApprove}
						onError={handleError}
						onInit={() => setLoading(false)}
					/>
				</PayPalSurface>
			</Box>
		</PayPalScriptProvider>
	);
};

export default PaypalButton;
